import React from 'react';
import {concatenate} from "services/string";
import Spinner from "components/Spinner";

enum Size {X_SMALL, SMALL, MEDIUM, LARGE, X_LARGE}
enum Color {PRIMARY, SECONDARY, WHITE, GREY}

type Props = {
    size?: Size;
    icon: (props: React.SVGProps<SVGSVGElement>) => JSX.Element
    color?: Color
    showSpinner?: boolean
    onClick?: () => any,
    disabled?: boolean,
}

const sizeClasses: Record<Size, string> = {
    [Size.X_SMALL]: 'p-1 md:1.5',
    [Size.SMALL]: 'p-1.5 md:2',
    [Size.MEDIUM]: 'p-2 md:2',
    [Size.LARGE]: 'p-2 md:3',
    [Size.X_LARGE]: 'p-3 md:3',
}

const iconSizeClasses: Record<Size, string> = {
    [Size.X_SMALL]: 'h-5 w-5 md:h-5 md:w-5',
    [Size.SMALL]: 'h-5 w-5 md:h-5 md:w-5',
    [Size.MEDIUM]: 'h-5 w-5 md:h-6 md:w-6',
    [Size.LARGE]: 'h-6 w-6 md:h-6 md:w-6',
    [Size.X_LARGE]: 'h-6 w-6 md:h-6 md:w-6',
}

const colorClasses: Record<Color, string> = {
    [Color.PRIMARY]: 'bg-brand hover:bg-gradient-white-overlay focus-visible:bg-gradient-white-overlay focus-visible:ring-brand',
    [Color.SECONDARY]: 'bg-[#d956fc] hover:bg-[#c604fb] focus-visible:bg-[#c604fb] focus-visible:ring-[#c604fb]',
    [Color.WHITE]: 'bg-white-900 hover:bg-gray-200 focus-visible:bg-gray-200 focus-visible:ring-gray-200',
    [Color.GREY]: 'bg-gray-200 hover:bg-gray-300 focus-visible:bg-gray-300 focus-visible:ring-gray-300',
}

const iconColorClasses: Record<Color, string> = {
    [Color.PRIMARY]: 'text-white',
    [Color.SECONDARY]: 'text-white',
    [Color.WHITE]: 'text-gray-600',
    [Color.GREY]: 'text-gray-600',
}

function CircularButton(props: Props) {
    const {size = Size.MEDIUM, color = Color.PRIMARY, showSpinner = false, onClick, disabled = false} = props
    const Icon = props.icon

    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            className={concatenate(
                sizeClasses[size],
                colorClasses[color],
                'inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 transition-all duration-200',
                disabled && 'opacity-50 cursor-not-allowed'
            )}
        >
            {showSpinner?
                <Spinner color={Spinner.colors.LIGHT} size={Spinner.sizes.SMALL}/> :
                <Icon className={concatenate(iconSizeClasses[size], iconColorClasses[color])} aria-hidden="true" />
            }
        </button>
    );
}

CircularButton.sizes = Size;
CircularButton.colors = Color;

export default CircularButton;